// Radar render pass. Ported 1:1 out of `canvas.ts`'s `draw()`; only
// `this.ctx` → an explicit `frame.ctx`.
import type { World } from '../sim/index.ts';
import { edgePoint, aircraftType, clamp, EDGE_MARGIN } from '../sim/index.ts';
import { hexA, reduceMotion } from './theme.ts';
import type { FrameCtx } from './canvas.ts';

/**
 * The radar is one map, not two. Everything on it — the field, the strips,
 * the traffic, the arrival still outside — is placed by the same scale, so a
 * dot means one thing: that is where that aircraft is. An arrival's dot sits
 * on the gate it will actually come through, and when it comes through, the
 * live dot is already there: nothing jumps.
 *
 * It used to work two different ways at once. Traffic was mapped uniformly
 * but at half the scale it needed (`R / max(W,H)` where the half-extent
 * called for `2R / max(W,H)`), so the whole field huddled in the middle of
 * the disc and aircraft a screen apart looked stacked. Arrivals ignored that
 * mapping entirely and were placed by their *bearing from the screen centre*
 * at a radius standing in for their ETA — a different projection, so the dot
 * teleported the moment the aircraft appeared. And the ETA radius pinned at
 * its inner stop as soon as an arrival's ETA passed, which the spawn gap
 * and the gate rule both routinely delay, leaving dots parked in a ring
 * doing nothing. Read together those three are the "broken, laggy" radar.
 *
 * How soon an arrival is due is told by weight instead of by position:
 * faint and small far out, solid and full size on the way in.
 */
export function drawRadar(frame: FrameCtx, world: World): void {
  const { ctx } = frame;
  const { W, H } = world;
  const R = 46, cx = W - R - 16, cy = H - R - 42;
  // one scale for the whole disc: the arrival gates sit EDGE_MARGIN outside
  // the field, and they are the outermost thing the radar has to hold
  const k = (R - 5) / (Math.max(W, H) / 2 + EDGE_MARGIN);
  const mx = (x: number): number => cx + (x - W / 2) * k;
  const my = (y: number): number => cy + (y - H / 2) * k;

  ctx.save();
  ctx.fillStyle = 'rgba(255,255,255,0.72)';
  ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = 'rgba(59,59,59,0.35)'; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.stroke();

  if (!reduceMotion) {
    const a = performance.now() / 1500;
    ctx.strokeStyle = 'rgba(59,59,59,0.14)';
    ctx.beginPath(); ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(a) * R, cy + Math.sin(a) * R); ctx.stroke();
  }

  // the field itself: the edge an arrival crosses to become your problem
  ctx.strokeStyle = 'rgba(59,59,59,0.16)'; ctx.lineWidth = 1;
  ctx.strokeRect(mx(0), my(0), W * k, H * k);
  // and the strips, so the disc has a north
  ctx.strokeStyle = 'rgba(59,59,59,0.30)'; ctx.lineWidth = 1.5;
  ctx.beginPath();
  for (const r of world.runways) { ctx.moveTo(mx(r.x1), my(r.y1)); ctx.lineTo(mx(r.x2), my(r.y2)); }
  ctx.stroke();
  for (const pd of world.pads) {
    ctx.beginPath(); ctx.arc(mx(pd.x), my(pd.y), 1.6, 0, Math.PI * 2); ctx.stroke();
  }

  // inbound arrivals, on the gate they will come through — under the live
  // traffic, because traffic already in the air is the more urgent read
  for (const s of world.schedule) {
    const ep = edgePoint(world, s.side, s.edgePos);
    const t = clamp((s.eta - world.elapsed) / 10, 0, 1); // 1 = far out
    const type = aircraftType(s.typeId);
    ctx.fillStyle = hexA(type.color, 0.9 - 0.55 * t);
    ctx.beginPath();
    ctx.arc(mx(ep.x), my(ep.y), (1.1 + 1.3 * (1 - t)) * type.scale, 0, Math.PI * 2);
    ctx.fill();
  }

  // live traffic
  for (const p of world.planes) {
    if (p.rollout > 0.6) continue;
    const d = Math.hypot(mx(p.x) - cx, my(p.y) - cy), max = R - 3;
    let dx = mx(p.x) - cx, dy = my(p.y) - cy;
    if (d > max) { dx *= max / d; dy *= max / d; } // a plane that has wrapped past the margin
    ctx.fillStyle = hexA(p.type.color, 0.95);
    ctx.beginPath(); ctx.arc(cx + dx, cy + dy, 2.4 * p.type.scale, 0, Math.PI * 2); ctx.fill();
  }
  ctx.restore();
}
